import { RailIcon } from "../experience/RailIcon";
import { useEffect, useRef, useState } from "react";
import { documentPdfFilename } from "../../shared/document-filename";
import { useSession } from "../auth/session";

interface PdfDocument { id: string; number?: string | null; version: number }

async function loadDocumentPdf(getToken: () => Promise<string | null>, kind: "invoice" | "estimate", document: PdfDocument, signal?: AbortSignal) {
  const token = await getToken();
  if (!token) throw new Error("Your session is unavailable. Sign in and try again.");
  const response = await fetch(`/api/${kind}s/${document.id}/pdf?view=1`, { headers: { authorization: `Bearer ${token}` }, signal });
  if (!response.ok) {
    const problem = await response.json() as { error?: string };
    throw new Error(problem.error ?? "The PDF could not be opened.");
  }
  const blob = await response.blob();
  signal?.throwIfAborted();
  const filename = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? documentPdfFilename(kind, document.number);
  return { url: URL.createObjectURL(new File([blob], filename, { type: "application/pdf" })), filename };
}

export function DocumentPdfAction({ kind, documentId, number, version, onPrepareDocument, disabled = false, onError, menuItem = false }: { kind: "invoice" | "estimate"; documentId?: string; number?: string | null; version?: number; onPrepareDocument?: () => Promise<PdfDocument>; disabled?: boolean; onError?: (message: string) => void; menuItem?: boolean }) {
  const { getToken } = useSession();
  const tokenRef = useRef(getToken); tokenRef.current = getToken;
  const [pdf, setPdf] = useState<{ key: string; url: string; filename: string } | null>(null);
  const [viewing, setViewing] = useState(false);
  const preparedUrls = useRef<string[]>([]);
  const viewController = useRef<AbortController | null>(null);
  const key = `${kind}:${documentId}:${version}`;
  useEffect(() => () => { viewController.current?.abort(); for (const url of preparedUrls.current) URL.revokeObjectURL(url); }, []);
  useEffect(() => {
    if (onPrepareDocument || !documentId || version === undefined) return;
    const controller = new AbortController();
    let url: string | undefined;
    void (async () => {
      const result = await loadDocumentPdf(tokenRef.current, kind, { id: documentId, number, version }, controller.signal);
      url = result.url;
      setPdf({ key, ...result });
    })().catch(() => { /* Keep the action unavailable when no valid PDF can be retrieved. */ });
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [kind, documentId, version, number, key, Boolean(onPrepareDocument)]);
  async function viewPreparedPdf() {
    if (!onPrepareDocument || viewing || disabled) return;
    const tab = window.open("about:blank", "_blank");
    if (!tab) { onError?.("Allow a new browser tab to view the PDF."); return; }
    tab.opener = null;
    const controller = new AbortController();
    viewController.current = controller;
    setViewing(true);
    try {
      const document = await onPrepareDocument();
      controller.signal.throwIfAborted();
      const result = await loadDocumentPdf(tokenRef.current, kind, document, controller.signal);
      preparedUrls.current.push(result.url);
      tab.location.href = result.url;
    } catch (error) {
      tab.close();
      if (!controller.signal.aborted) onError?.(error instanceof Error ? error.message : "The PDF could not be opened.");
    } finally { setViewing(false); }
  }
  if (onPrepareDocument) return <button className="invoice-register-return" type="button" disabled={disabled || viewing} aria-busy={viewing || undefined} aria-label={`View ${kind} PDF`} onClick={() => void viewPreparedPdf()}>View PDF</button>;
  if (pdf?.key !== key) return null;
  return <a role={menuItem ? "menuitem" : undefined} className="invoice-register-return" href={pdf.url} target="_blank" rel="noopener noreferrer" aria-label={`View ${kind} PDF`} title={pdf.filename}>{menuItem ? <RailIcon name="pdf"/> : null}View PDF</a>;
}
